import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, HeartHandshake } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { findArticle } from "@/data/articles";
import { logError } from "@/lib/log";

// Matching guide after a hard check-in (shown for 24 hours, until dismissed)
export const ArticlePick = () => {
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/article-pick").then((r) => setPick(r.data.pick)).catch((e) => logError("Article pick failed:", e));
  }, []);
  const a = pick && findArticle(pick.slug);
  if (!a) return null;

  const dismiss = () => {
    setPick(null);
    api.post("/article-pick/dismiss", { checkin_id: pick.checkin_id }).catch((e) => logError("Dismiss failed:", e));
  };

  const tell = async () => {
    setBusy(true);
    try { const { data } = await api.post("/article-pick/tell-supporters"); toast.success(data.message); setPick((p) => ({ ...p, told_today: true })); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mm-card overflow-hidden flex flex-col sm:flex-row mb-6 mm-fade-up" data-testid="article-pick">
      <img src={a.image} alt={a.imageAlt} className="sm:w-56 h-40 sm:h-auto object-cover" />
      <div className="p-5 flex-1">
        <div className="text-xs font-semibold text-mm-primary uppercase tracking-wide flex items-center gap-1.5"><BookOpen size={13} />A guide for today</div>
        <div className="font-semibold text-lg mt-1 leading-snug" data-testid="article-pick-title">{a.title}</div>
        <p className="text-sm text-mm-muted mt-1" data-testid="article-pick-reason">{pick.reason}, so this might help. {a.readMins} min read.</p>
        {pick.saved_note && findArticle(pick.saved_note.slug) && (
          <Link to={`/articles/${pick.saved_note.slug}`} className="block mm-soft p-3 rounded-2xl mt-3 text-sm break-words [overflow-wrap:anywhere] hover:bg-mm-primary/10 transition-colors" data-testid="article-pick-saved-note">
            <span className="text-xs text-mm-muted block">{pick.saved_note.same_topic ? "Last time something similar helped. Your note" : "Your note"} on “{findArticle(pick.saved_note.slug).title}”:</span>
            “{pick.saved_note.note}”
          </Link>
        )}
        {pick.can_tell && !pick.told_today && <p className="text-xs text-mm-muted mt-1">"Tell my supporters" sends a gentle "a kind note today could help" email, with no details.</p>}
        <div className="flex flex-wrap gap-2 mt-4">
          <Link to={`/articles/${a.slug}`} className="mm-btn !py-2 !px-4 text-sm" data-testid="article-pick-read">Read the guide</Link>
          {pick.can_tell && (pick.told_today
            ? <span className="text-sm text-mm-primary font-medium self-center px-2" data-testid="article-pick-told">💜 Your supporters know about today</span>
            : <button className="mm-btn-ghost !py-2 !px-4 text-sm bg-mm-surface" onClick={tell} disabled={busy} data-testid="article-pick-tell"><HeartHandshake size={15} />Tell my supporters</button>)}
          <button className="mm-btn-ghost !py-2 !px-4 text-sm" onClick={dismiss} data-testid="article-pick-dismiss">Not now</button>
        </div>
      </div>
    </div>
  );
};
